import { useEffect, useRef } from "react";
import * as THREE from "three";
import { EffectComposer } from "three/addons/postprocessing/EffectComposer.js";
import { RenderPass } from "three/addons/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/addons/postprocessing/UnrealBloomPass.js";
import { ShaderPass } from "three/addons/postprocessing/ShaderPass.js";
import { SMAAPass } from "three/addons/postprocessing/SMAAPass.js";
import { OutputPass } from "three/addons/postprocessing/OutputPass.js";
import GUI from "lil-gui";

type TrailSceneProps = {
  showGui?: boolean;
};

// Custom curve: straight floor -> arc bend -> vertical wall
class CycCurve extends THREE.Curve<THREE.Vector3> {
  x: number;
  zStart: number;
  zBend: number;
  radius: number;
  yEnd: number;
  L_flat: number;
  L_arc: number;
  L_up: number;
  totalLength: number;

  constructor(
    x: number,
    zStart: number,
    zBend: number,
    radius: number,
    yEnd: number,
  ) {
    super();
    this.x = x;
    this.zStart = zStart;
    this.zBend = zBend;
    this.radius = radius;
    this.yEnd = yEnd;

    this.L_flat = Math.abs(zStart - (zBend + radius));
    this.L_arc = Math.PI * radius * 0.5;
    this.L_up = Math.max(0.1, yEnd - radius);
    this.totalLength = this.L_flat + this.L_arc + this.L_up;
  }

  getPoint(t: number, optionalTarget = new THREE.Vector3()): THREE.Vector3 {
    const d = t * this.totalLength;
    let py = 0;
    let pz = 0;

    if (d <= this.L_flat) {
      pz = this.zStart - d;
    } else if (d <= this.L_flat + this.L_arc) {
      const norm = (d - this.L_flat) / this.L_arc;
      const eased = norm * norm * (3.0 - 2.0 * norm);
      const angle = (norm * 0.4 + eased * 0.6) * (Math.PI * 0.5);
      py = this.radius * (1.0 - Math.cos(angle));
      pz = this.zBend + this.radius - Math.sin(angle) * this.radius;
    } else {
      py = this.radius + (d - (this.L_flat + this.L_arc));
      pz = this.zBend;
    }
    return optionalTarget.set(this.x, py, pz);
  }
}

export default function TrailScene({ showGui = false }: TrailSceneProps) {
  const mountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!mountRef.current) return;
    const mount = mountRef.current;

    // --- Config ---
    const config = {
      dpr: Math.min(window.devicePixelRatio || 1, 1.5),
      exposure: 3.6505,
      brightness: 4.0131,
      bloomStrength: 0.2025,
      bloomRadius: 0.294,
      bloomThreshold: 0.0,
      speedMultiplier: 0.1,
      inverseMotion: false,
      linesCount: 100,
      dotDensity: 70,
      dotSize: 0.25,
      dotSpeed: 1.5,
      blurStrength: 3.5,
      arcRadius: 10.0,
      bendStartZ: -150.0,
      floorLength: 132.75,
      wallHeight: 200.0,
      color0: "#004c94",
      color1: "#2e89ff",
      color2: "#003994",
      color3: "#004bad",
      color4: "#ff5900",
    };

    // --- Scene Setup ---
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);

    const camera = new THREE.PerspectiveCamera(
      55,
      mount.clientWidth / mount.clientHeight,
      1,
      2000,
    );
    camera.position.set(0, 20, 140);
    camera.lookAt(0, 20, -50);

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: "high-performance",
    });
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    renderer.setPixelRatio(config.dpr);
    renderer.toneMapping = THREE.LinearToneMapping;
    renderer.toneMappingExposure = config.exposure;
    mount.appendChild(renderer.domElement);
    renderer.domElement.style.display = "block";
    renderer.domElement.style.width = "100%";
    renderer.domElement.style.height = "100%";

    const renderTarget = new THREE.WebGLRenderTarget(
      mount.clientWidth * config.dpr,
      mount.clientHeight * config.dpr,
      {
        type: THREE.HalfFloatType,
        format: THREE.RGBAFormat,
      },
    );

    const renderScene = new RenderPass(scene, camera);
    const smaaPass = new SMAAPass(
      mount.clientWidth * config.dpr,
      mount.clientHeight * config.dpr,
    );
    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(mount.clientWidth, mount.clientHeight),
      config.bloomStrength,
      config.bloomRadius,
      config.bloomThreshold,
    );

    const foregroundBlurShader = {
      uniforms: {
        tDiffuse: { value: null },
        resolution: {
          value: new THREE.Vector2(
            mount.clientWidth * config.dpr,
            mount.clientHeight * config.dpr,
          ),
        },
        blurStrength: { value: config.blurStrength },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D tDiffuse;
        uniform vec2 resolution;
        uniform float blurStrength;
        varying vec2 vUv;
        void main() {
          float mask = 1.0 - smoothstep(0.0, 0.35, vUv.y);
          float radius = mask * blurStrength;
          if (radius < 0.1) {
            gl_FragColor = texture2D(tDiffuse, vUv);
          } else {
            vec4 color = vec4(0.0);
            float total = 0.0;
            const float GA = 2.3999632;
            for (int i = 0; i < 32; i++) {
              float f = float(i);
              float r = sqrt(f) * radius;
              float theta = f * GA;
              vec2 offset = vec2(cos(theta), sin(theta)) * (r / resolution);
              color += texture2D(tDiffuse, vUv + offset);
              total += 1.0;
            }
            gl_FragColor = color / total;
          }
        }
      `,
    };
    const blurPass = new ShaderPass(foregroundBlurShader);
    const outputPass = new OutputPass();

    const composer = new EffectComposer(renderer, renderTarget);
    composer.setPixelRatio(config.dpr);
    composer.addPass(renderScene);
    composer.addPass(smaaPass);
    composer.addPass(bloomPass);
    composer.addPass(blurPass);
    composer.addPass(outputPass);

    // --- Floor ---
    const floorMat = new THREE.MeshBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 0.85,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const floorMesh = new THREE.Mesh(new THREE.BufferGeometry(), floorMat);
    floorMesh.position.set(0, -0.5, -0.5);
    floorMesh.renderOrder = 1;
    scene.add(floorMesh);

    // --- Trails ---
    const trailObjects: {
      mesh: THREE.Mesh;
      refMesh: THREE.Mesh;
      startX: number;
      thickness: number;
    }[] = [];
    const trailMaterials: Record<string, { value: unknown }>[] = [];

    const group = new THREE.Group();
    scene.add(group);

    const vertexShader = `
      varying vec2 vUv;
      varying vec3 vNormal;
      varying vec3 vViewPosition;
      void main() {
        vUv = uv;
        vNormal = normalMatrix * normal;
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        vViewPosition = -mvPosition.xyz;
        gl_Position = projectionMatrix * mvPosition;
      }
    `;

    const fragmentShader = `
      varying vec2 vUv;
      varying vec3 vNormal;
      varying vec3 vViewPosition;
      uniform float uTime;
      uniform vec3 uColor;
      uniform float uSpeed;
      uniform float uOffset;
      uniform float uTailLength;
      uniform float uIntensityMultiplier;
      uniform float uBendUv;
      uniform float uIsReflection;
      uniform float uDotDensity;
      uniform float uDotSize;
      uniform float uDotSpeed;
      uniform float uBrightness;
      uniform float uDirection;

      float hash(vec2 p) {
        return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453);
      }

      void main() {
        float t = fract(uTime * uSpeed * uDirection + uOffset);
        float dist = fract((t - vUv.x) * uDirection + 1.0);

        float baseAlpha = smoothstep(uTailLength, 0.0, dist);
        baseAlpha = pow(max(0.0, baseAlpha), 1.2);

        vec3 viewDir = normalize(vViewPosition);
        float fresnel = abs(dot(normalize(vNormal), viewDir));
        float edgeSoftness = smoothstep(0.0, 0.02, fresnel);
        baseAlpha *= edgeSoftness;

        float core = pow(max(0.0, baseAlpha), 3.0) * 1.5;

        float movingUV = vUv.x - (uTime * uSpeed * uDotSpeed * uDirection) - uOffset;
        float signalPos = movingUV * uDotDensity;
        float dotId = floor(signalPos);
        float dotLocal = fract(signalPos);

        float distToCenter = length(vec2((dotLocal - 0.5) * 2.0, (fract(vUv.y + 0.5) - 0.5) * 6.0));
        float dotShape = 1.0 - smoothstep(0.0, max(0.001, uDotSize), distToCenter);
        float dotFinal = dotShape * step(0.6, hash(vec2(dotId, uOffset))) * (sin(uTime * 4.0 + hash(vec2(dotId)) * 6.28) * 0.3 + 0.7) * baseAlpha;

        if (uIsReflection > 0.5) {
          float refFade = 1.0 - smoothstep(uBendUv - 0.015, uBendUv, vUv.x);
          baseAlpha *= refFade;
          core *= refFade;
          dotFinal *= refFade * 0.1;
          baseAlpha = pow(max(0.0, baseAlpha), 0.5) * (0.7 + hash(vUv * 300.0 + uTime * 0.05) * 0.3);
          core *= 0.3;
        }

        vec3 trailColor = uColor * (baseAlpha + core * 1.5) * uIntensityMultiplier * uBrightness;
        vec3 rgb = trailColor / max(1.0 - clamp(dotFinal * 1.8, 0.0, 0.95), 0.001) + uColor * dotFinal * 2.5 * uIntensityMultiplier * uBrightness;

        gl_FragColor = vec4(rgb, (baseAlpha + dotFinal) * uIntensityMultiplier);
      }
    `;

    const getColor = (idx: number) =>
      new THREE.Color(
        (config as unknown as Record<string, string>)[`color${idx}`],
      );

    for (let i = 0; i < config.linesCount; i += 1) {
      const normIdx = (i / (config.linesCount - 1)) * 2 - 1;
      const linearPos = normIdx;
      const expPos = Math.sign(normIdx) * Math.pow(Math.abs(normIdx), 1.2);
      let startX = (linearPos * 0.5 + expPos * 0.5) * 80;
      startX += (Math.random() - 0.5) * 2.0;

      const thickness = Math.random() * 0.2 + 0.1;
      const colorIdx = Math.floor(Math.random() * 5);

      const uniforms = {
        uTime: { value: 0 },
        uColor: { value: getColor(colorIdx) },
        uColorIndex: { value: colorIdx },
        uSpeed: { value: Math.random() * 0.5 + 0.2 },
        uOffset: { value: Math.random() },
        uTailLength: { value: Math.random() * 0.4 + 0.3 },
        uIntensityMultiplier: { value: 1.0 },
        uBendUv: { value: 0.0 },
        uIsReflection: { value: 0.0 },
        uDotDensity: { value: config.dotDensity },
        uDotSize: { value: config.dotSize },
        uDotSpeed: { value: config.dotSpeed },
        uBrightness: { value: config.brightness },
        uDirection: { value: config.inverseMotion ? -1.0 : 1.0 },
      };

      const material = new THREE.ShaderMaterial({
        vertexShader,
        fragmentShader,
        uniforms,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });

      const mesh = new THREE.Mesh(new THREE.BufferGeometry(), material);
      const refMaterial = material.clone();
      refMaterial.uniforms.uIntensityMultiplier.value = 0.4;
      refMaterial.uniforms.uIsReflection.value = 1.0;
      const refMesh = new THREE.Mesh(new THREE.BufferGeometry(), refMaterial);
      refMesh.scale.y = -1;
      refMesh.position.y = -1.0;

      group.add(mesh, refMesh);
      trailMaterials.push(
        material.uniforms as unknown as Record<string, { value: unknown }>,
        refMaterial.uniforms as unknown as Record<string, { value: unknown }>,
      );
      trailObjects.push({ mesh, refMesh, startX, thickness });
    }

    // --- Geometry build ---
    const updateGeometries = () => {
      if (floorMesh.geometry) floorMesh.geometry.dispose();
      const floorGeo = new THREE.PlaneGeometry(1000, 1000, 1, 1500);
      floorGeo.rotateX(-Math.PI * 0.5);
      const pos = floorGeo.attributes.position.array as Float32Array;

      for (let i = 0; i < pos.length; i += 3) {
        if (pos[i + 2] < config.bendStartZ) {
          const d = config.bendStartZ - pos[i + 2];
          const maxA = config.arcRadius * Math.PI * 0.5;
          if (d < maxA) {
            const n = d / maxA;
            const a =
              (n * 0.4 + n * n * (3.0 - 2.0 * n) * 0.6) * (Math.PI * 0.5);
            pos[i + 1] = config.arcRadius * (1.0 - Math.cos(a));
            pos[i + 2] = config.bendStartZ - Math.sin(a) * config.arcRadius;
          } else {
            pos[i + 1] = config.arcRadius + (d - maxA);
            pos[i + 2] = config.bendStartZ - config.arcRadius;
          }
        }
      }
      floorGeo.computeVertexNormals();
      floorMesh.geometry = floorGeo;

      const bendUv =
        Math.abs(config.floorLength - config.bendStartZ) /
        (Math.abs(config.floorLength - config.bendStartZ) +
          Math.PI * config.arcRadius * 0.5 +
          Math.max(0.1, config.wallHeight - config.arcRadius));

      trailObjects.forEach((obj) => {
        const path = new CycCurve(
          obj.startX,
          config.floorLength,
          config.bendStartZ - config.arcRadius,
          config.arcRadius,
          config.wallHeight,
        );
        const geo = new THREE.TubeGeometry(path, 200, obj.thickness, 8, false);
        if (obj.mesh.geometry) obj.mesh.geometry.dispose();
        obj.mesh.geometry = geo;
        obj.refMesh.geometry = geo;
        (obj.mesh.material as THREE.ShaderMaterial).uniforms.uBendUv.value =
          bendUv;
        (obj.refMesh.material as THREE.ShaderMaterial).uniforms.uBendUv.value =
          bendUv;
      });
    };

    updateGeometries();

    // --- GUI ---
    let gui: GUI | null = null;
    if (showGui) {
      gui = new GUI({ title: "Scene Settings" });

      const sys = gui.addFolder("System");
      sys
        .add(config, "dpr", 0.5, 2.0, 0.1)
        .name("DPR (Pixel Ratio)")
        .onChange((v: number) => {
          renderer.setPixelRatio(v);
          composer.setPixelRatio(v);
          smaaPass.setSize(mount.clientWidth * v, mount.clientHeight * v);
          handleResize();
        });
      sys
        .add(config, "exposure", 0.1, 5.0)
        .name("Exposure")
        .onChange((v: number) => {
          renderer.toneMappingExposure = v;
        });
      sys
        .add(config, "brightness", 0.1, 5.0)
        .name("Trail Brightness")
        .onChange((v: number) => {
          trailMaterials.forEach((m) => {
            (m.uBrightness as { value: number }).value = v;
          });
        });

      const post = gui.addFolder("Post-Processing (Bloom)");
      post
        .add(config, "bloomStrength", 0.0, 1.5)
        .name("Strength")
        .onChange((v: number) => {
          bloomPass.strength = v;
        });
      post
        .add(config, "bloomRadius", 0.0, 1.5)
        .name("Radius")
        .onChange((v: number) => {
          bloomPass.radius = v;
        });
      post
        .add(config, "bloomThreshold", 0.0, 1.0)
        .name("Threshold")
        .onChange((v: number) => {
          bloomPass.threshold = v;
        });
      post
        .add(config, "blurStrength", 0.0, 20.0)
        .name("Foreground Blur")
        .onChange((v: number) => {
          blurPass.uniforms.blurStrength.value = v;
        });

      const geom = gui.addFolder("Geometry Settings");
      geom
        .add(config, "arcRadius", 10.0, 200.0)
        .name("Arc Radius")
        .onChange(updateGeometries);
      geom
        .add(config, "bendStartZ", -300.0, 100.0)
        .name("Bend Start Z")
        .onChange(updateGeometries);
      geom
        .add(config, "floorLength", 50.0, 500.0)
        .name("Floor Length")
        .onChange(updateGeometries);
      geom
        .add(config, "wallHeight", 50.0, 1000.0)
        .name("Wall Height")
        .onChange(updateGeometries);

      const cols = gui.addFolder("Trail Colors");
      for (let i = 0; i < 5; i += 1) {
        const idx = i;
        cols
          .addColor(config, `color${idx}` as keyof typeof config)
          .name(`Color ${idx + 1}`)
          .onChange((v: string) => {
            const c = new THREE.Color(v);
            trailMaterials.forEach((m) => {
              if ((m.uColorIndex as { value: number }).value === idx) {
                (m.uColor as { value: THREE.Color }).value.copy(c);
              }
            });
          });
      }

      const anim = gui.addFolder("Animation");
      anim.add(config, "speedMultiplier", 0.0, 1.0).name("Global Speed");
      anim
        .add(config, "inverseMotion")
        .name("Inverse Motion")
        .onChange((v: boolean) => {
          const dir = v ? -1.0 : 1.0;
          trailMaterials.forEach((m) => {
            (m.uDirection as { value: number }).value = dir;
          });
        });

      const dots = gui.addFolder("Signal Dots");
      const setDots = (u: string, v: number) =>
        trailMaterials.forEach((m) => {
          (m[u] as { value: number }).value = v;
        });
      dots
        .add(config, "dotDensity", 10.0, 100.0)
        .name("Density")
        .onChange((v: number) => setDots("uDotDensity", v));
      dots
        .add(config, "dotSize", 0.05, 0.5)
        .name("Dot Size")
        .onChange((v: number) => setDots("uDotSize", v));
      dots
        .add(config, "dotSpeed", 0.5, 3.0)
        .name("Speed Multiplier")
        .onChange((v: number) => setDots("uDotSpeed", v));

      gui.close();
    }

    // --- Resize ---
    const handleResize = () => {
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
      composer.setSize(w, h);
      smaaPass.setSize(w * config.dpr, h * config.dpr);
      blurPass.uniforms.resolution.value.set(w * config.dpr, h * config.dpr);
    };
    window.addEventListener("resize", handleResize);

    // --- Scroll-driven interaction ---
    // Base state: particles always play FORWARD (direction = 1).
    // Scrolling UP momentarily reverses them (rewind effect) only while
    // the user is actively scrolling up. After a short idle window the
    // direction snaps back to forward automatically.
    const BASE_DIRECTION = 1;
    let direction = BASE_DIRECTION;
    let rewindHoldTimer = 0; // seconds; while > 0 we keep direction = -1
    let scrollBoost = 0;
    let targetBoost = 0;

    const applyDirection = (dir: number) => {
      if (dir === direction) return;
      direction = dir;
      trailMaterials.forEach((m) => {
        (m.uDirection as { value: number }).value = direction;
      });
    };

    const triggerScrollInput = (deltaY: number, intensityDivisor: number) => {
      if (deltaY < 0) {
        // Scrolling UP -> momentary rewind on top of the base forward play
        applyDirection(-1);
        rewindHoldTimer = 0.35;
      } else if (deltaY > 0) {
        // Scrolling DOWN -> keep forward, just give a speed boost
        rewindHoldTimer = 0;
        applyDirection(BASE_DIRECTION);
      }
      const intensity = Math.min(Math.abs(deltaY) / intensityDivisor, 1);
      targetBoost = Math.max(targetBoost, intensity * 1.2);
    };

    const handleWheel = (e: WheelEvent) => {
      triggerScrollInput(e.deltaY, 100);
    };
    window.addEventListener("wheel", handleWheel, { passive: true });

    // Touch support for mobile scroll gestures
    let lastTouchY: number | null = null;
    const handleTouchStart = (e: TouchEvent) => {
      lastTouchY = e.touches[0]?.clientY ?? null;
    };
    const handleTouchMove = (e: TouchEvent) => {
      if (lastTouchY === null) return;
      const y = e.touches[0]?.clientY ?? lastTouchY;
      const deltaY = lastTouchY - y;
      lastTouchY = y;
      triggerScrollInput(deltaY, 30);
    };
    const handleTouchEnd = () => {
      lastTouchY = null;
    };
    window.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: true });
    window.addEventListener("touchend", handleTouchEnd, { passive: true });

    // --- Animate ---
    const clock = new THREE.Clock();
    let globalTime = 0;
    let frameId = 0;

    const animate = () => {
      frameId = requestAnimationFrame(animate);
      const delta = clock.getDelta();

      // Smoothly ease the boost toward the target, then decay the target
      scrollBoost += (targetBoost - scrollBoost) * Math.min(delta * 6, 1);
      targetBoost *= Math.max(0, 1 - delta * 2.5);

      // Count down the rewind hold; snap back to forward when it expires
      if (rewindHoldTimer > 0) {
        rewindHoldTimer -= delta;
        if (rewindHoldTimer <= 0) {
          rewindHoldTimer = 0;
          applyDirection(BASE_DIRECTION);
        }
      }

      const effectiveSpeed = config.speedMultiplier + scrollBoost;
      globalTime += delta * effectiveSpeed;

      trailMaterials.forEach((m) => {
        (m.uTime as { value: number }).value = globalTime;
      });
      composer.render();
    };
    animate();

    // --- Cleanup ---
    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
      if (gui) gui.destroy();

      trailObjects.forEach((obj) => {
        obj.mesh.geometry.dispose();
        (obj.mesh.material as THREE.ShaderMaterial).dispose();
        (obj.refMesh.material as THREE.ShaderMaterial).dispose();
      });
      if (floorMesh.geometry) floorMesh.geometry.dispose();
      floorMat.dispose();
      renderTarget.dispose();
      composer.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, [showGui]);

  return (
    <div
      ref={mountRef}
      className="absolute inset-0 h-full w-full overflow-hidden bg-black"
    />
  );
}